import { useCallback, useContext, useEffect, useState } from 'react';
import type { FC } from 'react';

import Tooltip from '../Tooltip';
import { useElement, useI18n } from '../../hooks';
import Icon, { registerIcon } from '../Icon';
import * as locationIcon from '../Icon/icons/location.icon';
import Button from '../Button';

import MapsContext from './MapsContext';
import { getNavigatorPosition, loadMapsAPI } from './utils';

registerIcon(locationIcon);

interface OnChangeAddressValue {
  name: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

interface OnChangeCoordsValue {
  latitude: number;
  longitude: number;
}

type OnChangeValue = OnChangeAddressValue | OnChangeCoordsValue;

export type CurrentLocationButtonProps = {
  /** Callback fired after click on my current location button if location was found. */
  onResolve?: (value: OnChangeValue) => void;
  /** Callback fired when an error occurs. */
  onError?: (error: Error) => void;
};

const CurrentLocationButton: FC<CurrentLocationButtonProps> = ({
  onResolve,
  onError
}: CurrentLocationButtonProps) => {
  const t = useI18n();
  const { name, ...providerOpts } = useContext(MapsContext);
  const [currentLocationDisabled, setCurrentLocationDisabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [el, setEl] = useElement();

  const getCurrentLocation = useCallback(() => {
    setBusy(true);
    Promise.all([getNavigatorPosition(), loadMapsAPI(name, providerOpts)])
      .catch(error => {
        setCurrentLocationDisabled(true);
        throw error;
      })
      .then(([navigatorPosition, mapAPIObj]) => {
        return mapAPIObj.getAddress(navigatorPosition);
      })
      .then(address => {
        onResolve?.(address);
      })
      .catch(error => onError?.(error))
      .finally(() => setBusy(false));
  }, [onResolve, onError]);

  useEffect(() => {
    navigator.permissions?.query({ name: 'geolocation' }).then(result => {
      if (result.state === 'denied') {
        setCurrentLocationDisabled(true);
      }
    });
  }, []);

  return (
    <>
      <Button
        ref={setEl}
        variant='simple'
        icon
        onClick={getCurrentLocation}
        aria-label={t('my_current_location_button_a11y')}
        disabled={currentLocationDisabled}
        loading={busy}
      >
        <Icon name='location' />
      </Button>
      {el && currentLocationDisabled && (
        <Tooltip shown target={el}>
          {t('allow_location_permissions_text')}
        </Tooltip>
      )}
    </>
  );
};
export default CurrentLocationButton;
